/**
 * Sales overview (/dashboard). One period + one comparison (useSalesScope, kept in the URL) drives
 * every section:
 *   header (title, period label, Upload sales for editors), SalesControlBar,
 *   KPI cards (Revenue / Units / Returns switch the chart metric; Returns also opens the ReturnsPanel; Target with month-end pace;
 *   Stock cover opens /stock-health), trend chart (click a day/week -> /daily-sales for that range
 *   with the page filters), "What changed" movers, platform share and brand -> category mix
 *   (click applies a page filter), and the top 10 products (row opens ProductDetailDrawer).
 * Data: GET /api/sales-hub?dimension=sku, /api/sales-hub/platforms, /api/sales-hub/leaderboard.
 */
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canEditModule, viewOnlyHint } from '../lib/permissions';
import { LoadError } from '../components/LoadError';
import { Button } from '../components/ui/button';
import { useSalesScope } from '../components/sales/useSalesScope';
import { useSalesData } from '../components/sales/salesApi';
import { SalesControlBar } from '../components/sales/SalesControlBar';
import { SalesUploadDialog } from '../components/sales/SalesUploadDialog';
import { SalesKpiRow } from '../components/sales/SalesMetrics';
import { SalesTrendChart } from '../components/sales/SalesCharts';
import { SalesMovers } from '../components/sales/SalesExceptions';
import { TopProductsTable } from '../components/sales/SalesPerformanceTable';
import { ProductDetailDrawer } from '../components/sales/ProductDetailDrawer';
import { ReturnsPanel } from '../components/sales/ReturnsPanel';
import { BrandCategoryMix, PlatformMix } from '../components/sales/overview/SalesMix';
import { dailyRecordsQuery } from '../components/sales/overview/trend';
import { ImportNotice, SalesPageHeader, importMessage } from '../components/sales/overview/SalesPageHeader';
import { dayCount, formatDateRange, number, shiftDay } from '../components/sales/format';

export default function SalesOverviewPage(props) {
  // SalesHubPage passes its scope so there is one useSalesScope per page; standalone use falls back to our own.
  const ownScope = useSalesScope();
  const salesScope = props?.salesScope || ownScope;
  const { scope, setScope, compareLabel } = salesScope;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const canEditSales = canEditModule(user, 'sales');
  const [metric, setMetric] = useState('revenue');
  const [openSku, setOpenSku] = useState(null);
  const [returnsOpen, setReturnsOpen] = useState(false);
  const [message, setMessage] = useState('');

  const hub = useSalesData('hub', scope, { dimension: 'sku' });
  const platforms = useSalesData('platforms', scope);
  const board = useSalesData('leaderboard', scope);
  const data = hub.data;
  const anyLoading = hub.loading || platforms.loading || board.loading;
  const refreshAll = () => { hub.refresh(); platforms.refresh(); board.refresh(); };

  const hasListFilter = scope.brand !== 'ALL' || scope.category !== 'ALL' || Boolean(scope.sku) || Boolean(scope.search);
  const noSales = data && !data.meta?.records && !(data.summary?.revenue > 0);
  const latest = data?.meta?.latest_sale_date;

  const imported = summary => {
    setMessage(importMessage(summary));
    if (summary.date_from && summary.date_to) {
      const fits = dayCount(summary.date_from, summary.date_to) <= 366;
      setScope({ date_from: fits ? summary.date_from : shiftDay(summary.date_to, -29), date_to: summary.date_to });
    }
    refreshAll();
  };
  const openDay = bucket => navigate(`/daily-sales?${dailyRecordsQuery(scope, bucket.from, bucket.to)}`);
  const toggle = (key, value) => setScope({ [key]: scope[key] === value ? '' : value });

  return (
    <div className="space-y-5" data-testid="sales-overview">
      <SalesPageHeader salesScope={salesScope} title="Sales overview" testId="sales-hub-heading">
        {canEditSales
          ? <SalesUploadDialog onImported={imported} />
          : <span className="self-center text-xs text-gray-600" data-testid="sales-view-only">View only · {viewOnlyHint(user)}</span>}
      </SalesPageHeader>

      <SalesControlBar salesScope={salesScope} brands={data?.options?.brands} categories={data?.options?.categories}
        onRefresh={() => { setMessage(''); refreshAll(); }} loading={anyLoading} />

      <ImportNotice message={message} onDismiss={() => setMessage('')} />
      {hub.error && <LoadError what="the sales overview" error={hub.error} onRetry={hub.refresh} retrying={hub.loading} testId="sales-report-error" />}

      {noSales && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-gray-300 bg-white p-4" data-testid="sales-no-records">
          <div>
            <p className="font-medium text-gray-900">No sales recorded for {formatDateRange(scope.date_from, scope.date_to)}</p>
            <p className="mt-0.5 text-sm text-gray-500">
              {latest ? `The latest sale on file is from ${formatDateRange(latest, latest, true)}. ` : 'Nothing has been uploaded yet. '}
              {canEditSales ? 'Upload a sales report or pick another period.' : 'Pick another period, or ask a manager to upload sales.'}
              {hasListFilter ? ' Filters are also narrowing the view.' : ''}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {latest && latest < scope.date_from && (
              <Button size="sm" variant="outline" onClick={() => setScope({ date_from: shiftDay(latest, -29), date_to: latest })} data-testid="sales-jump-latest">
                Show 30 days to {formatDateRange(latest, latest)}
              </Button>
            )}
            {salesScope.activeFilters.length > 0 && <Button size="sm" variant="ghost" onClick={salesScope.clearFilters}>Clear filters</Button>}
          </div>
        </div>
      )}

      {(data || hub.loading) && (
        <>
          <SalesKpiRow summary={data?.summary} trend={data?.trend} compareLabel={compareLabel} metric={metric} onMetric={setMetric}
            onStock={() => navigate('/stock-health')} onReturns={() => setReturnsOpen(true)} loading={hub.loading} hasListFilter={hasListFilter} />

          <SalesTrendChart trend={data?.trend || []} metric={metric} onMetric={setMetric} compareLabel={compareLabel}
            onSelect={openDay} loading={hub.loading} />

          <SalesMovers rows={data?.rows || []} compareLabel={compareLabel} onOpen={setOpenSku} loading={hub.loading} />

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <PlatformMix data={platforms.data} error={platforms.error} onRetry={platforms.refresh} loading={platforms.loading}
              active={scope.platform} onPick={code => toggle('platform', code)} />
            <BrandCategoryMix data={board.data} error={board.error} onRetry={board.refresh} loading={board.loading}
              activeBrand={scope.brand} activeCategory={scope.category}
              onBrand={brand => setScope(scope.brand === brand && scope.category === 'ALL' ? { brand: '' } : { brand, category: '' })}
              onCategory={(brand, category) => setScope(scope.brand === brand && scope.category === category ? { category: '' } : { brand, category })} />
          </div>

          <TopProductsTable rows={data?.rows || []} seeAllHref={`/sales/skus${location.search}`} onOpen={setOpenSku} loading={hub.loading} />

          {data && (
            <footer className="flex flex-wrap justify-between gap-2 border-t border-gray-200 pt-3 text-xs text-gray-500" data-testid="sales-data-footer">
              <span>{number(data.meta?.records)} sales records · {data.meta?.days_with_sales ?? 0} of {data.meta?.days ?? 0} days have sales{latest ? ` · latest sale ${formatDateRange(latest, latest, true)}` : ''}</span>
              <Link to="/returns" className="text-blue-700 hover:underline" data-testid="sales-manage-returns">Recorded returns →</Link>
              {(data.meta?.notices || []).length > 0 && (
                <ul className="w-full space-y-0.5" data-testid="sales-data-notices">{data.meta.notices.map(text => <li key={text}>{text}</li>)}</ul>
              )}
            </footer>
          )}
        </>
      )}

      <ReturnsPanel open={returnsOpen} onClose={() => setReturnsOpen(false)} scope={scope} compareLabel={compareLabel}
        onOpenSku={code => { setReturnsOpen(false); setOpenSku(code); }} />
      <ProductDetailDrawer sku={openSku} scope={scope} onClose={() => setOpenSku(null)}
        onPlatform={code => { setOpenSku(null); setScope({ platform: code }); }} />
    </div>
  );
}
